import { useCallback } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import {
  PencilIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { Well } from 'src/common/primitives/Well/Well';

import { editLineItems } from '../invoice.slice';
import type { HydratedTripLineItem } from './cells/types';
import { INVOICE_LINE_ITEM_TRIP_ID_MARKER } from './constants';
import type { InvoiceNestedLineItemWithId } from './InvoiceFormContext';
import { InvoiceLineItemList } from './InvoiceLineItemList';

interface InvoiceLineItemContainerProps {
  lineItems: InvoiceNestedLineItemWithId[];
  onAddLineItem: (item: HydratedTripLineItem) => void;
  onDeleteLineItem: (rowIndex: number, row: HydratedTripLineItem) => void;
}

export function InvoiceLineItemContainer({
  lineItems,
  onAddLineItem,
  onDeleteLineItem,
}: InvoiceLineItemContainerProps) {
  const trip = lineItems[0].trip;
  const dispatch = useAppDispatch();
  const editingLineItem = useAppSelector(
    (state) => state.invoice.editingLineItemGroup
  );

  const handleAddTripLineItem = useCallback(() => {
    onAddLineItem({
      trip,
      amount_cents: 0,
      description: '',
      line_item_type: 'modifier',
      invoice: '',
    });
  }, [trip, onAddLineItem]);

  const handleEditLineItems = useCallback(() => {
    if (editingLineItem === trip.id) {
      dispatch(editLineItems(''));
    } else {
      dispatch(editLineItems(trip.id));
    }
  }, [trip, dispatch, editingLineItem]);

  const isInvoiceLineItem = lineItems[0]?.trip.id.includes(
    INVOICE_LINE_ITEM_TRIP_ID_MARKER
  );

  return (
    <Well testId={`well-for-trip-${trip.id}`}>
      <div id={`scroll-anchor-${trip.id}`} data-testid="scroll-anchor" />
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="text-sm font-bold">
          {isInvoiceLineItem ? 'Adjustment' : 'Trip'}
        </div>
        <div
          className="flex flex-row gap-2"
          data-editingstate={
            editingLineItem === trip.id ? 'editing' : 'reading'
          }
        >
          <Button
            dataTestId="line-item-group-edit-button"
            className="flex gap-2 print:hidden"
            onClick={handleEditLineItems}
          >
            <PencilIcon className="w-5" />
            Edit
          </Button>
          <Button
            className="flex gap-2 print:hidden"
            dataTestId="line-item-group-delete-button"
            onClick={() => onDeleteLineItem(0, lineItems[0])}
          >
            <TrashIcon className="w-5" />
            Delete
          </Button>
        </div>
      </div>
      <InvoiceLineItemList
        lineItems={lineItems}
        onDeleteLineItem={onDeleteLineItem}
      />
      {editingLineItem === trip.id &&
      !trip.id.endsWith(INVOICE_LINE_ITEM_TRIP_ID_MARKER) ? (
        <div className="mt-2">
          <Button
            dataTestId="line-item-container-add-subitem-button"
            variant="secondary"
            className="flex gap-2 print:hidden"
            onClick={handleAddTripLineItem}
            buttonProps={{
              autoFocus: true,
            }}
          >
            <PlusCircleIcon className="w-5" />
            Add a subitem
          </Button>
        </div>
      ) : null}
    </Well>
  );
}
